// Phase 7 · Learning Loop.
// Friday brief viewer + lesson queue. Lessons can be edited by the owner
// before accept; owner-only decisions (accept / reject) write an audit row
// and promote accepted lessons through the Vault governance.
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import Shell from "../layout/Shell";
import { useAuth } from "../lib/auth";
import { api, formatApiError } from "../lib/api";
import { EmptyState, ErrorBanner, LoadingCard, Pill } from "../lib/ui";

function friendlyDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      timeZone: "America/New_York",
      month: "short", day: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch {
    return String(iso);
  }
}

const KIND_LABEL = {
  add_allowed: "Add allowed claim",
  change_voice_rule: "Change voice rule",
  tighten_pattern: "Tighten forbidden pattern",
  retire_claim: "Retire a claim",
};
const KIND_KEYS = Object.keys(KIND_LABEL);

export default function LearningLoop() {
  const { role } = useAuth();
  const canDecide = role === "owner";
  const canPropose = role === "owner" || role === "admin" || role === "editor";

  const [state, setState] = useState({ loading: true, error: null });
  const [brief, setBrief] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [filter, setFilter] = useState("proposed");
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState(null);
  const [editing, setEditing] = useState({}); // lesson_id -> draft
  const [reasonById, setReasonById] = useState({});

  const load = useCallback(async () => {
    setState({ loading: true, error: null });
    try {
      const q = filter === "all" ? "" : `?status=${filter}`;
      const [b, l] = await Promise.all([
        api.get("/learning/brief"),
        api.get(`/learning/lessons${q}`),
      ]);
      setBrief(b.data || null);
      setLessons(l.data?.rows || []);
      setState({ loading: false, error: null });
    } catch (e) {
      setState({ loading: false, error: formatApiError(e) });
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  function startEditing(l) {
    setEditing({
      ...editing,
      [l.id]: {
        summary: l.summary || "",
        kind: l.kind || "add_allowed",
        tags: (l.tags || []).join(", "),
      },
    });
  }

  function cancelEditing(id) {
    const next = { ...editing };
    delete next[id];
    setEditing(next);
  }

  async function saveEdit(l) {
    if (!canDecide) return;
    const draft = editing[l.id];
    if (!draft) return;
    setBusy(true);
    try {
      const tags = draft.tags.split(",").map((s) => s.trim()).filter(Boolean);
      await api.patch(`/learning/lessons/${l.id}`, {
        summary: draft.summary,
        kind: draft.kind,
        tags,
      });
      setFlash("Lesson updated · audit row written.");
      cancelEditing(l.id);
      await load();
    } catch (e) {
      setState((s) => ({ ...s, error: formatApiError(e) }));
    } finally {
      setBusy(false);
    }
  }

  async function decide(l, decision) {
    if (!canDecide) return;
    setBusy(true);
    try {
      await api.post(`/learning/lessons/${l.id}/decide`, {
        decision,
        reason: reasonById[l.id] || null,
      });
      setFlash(`Lesson ${decision}ed · audit row written.`);
      const nextR = { ...reasonById };
      delete nextR[l.id];
      setReasonById(nextR);
      await load();
    } catch (e) {
      setState((s) => ({ ...s, error: formatApiError(e) }));
    } finally {
      setBusy(false);
    }
  }

  if (state.loading) {
    return (
      <Shell title="Learning Loop">
        <LoadingCard label="Loading learning loop…" />
      </Shell>
    );
  }

  const winners = brief?.winners || [];
  const losers = brief?.losers || [];
  const pending = brief?.pending_lessons || [];

  return (
    <Shell title="Learning Loop">
      <div className="bo-page" data-testid="learning-page">
        {/* Header */}
        <section className="bo-card">
          <div className="bo-eyebrow">Phase 7 · Learning Loop</div>
          <h1 className="bo-serif" style={{ fontSize: 26, margin: "6px 0 8px" }}>
            Friday brief · lessons to accept or reject
          </h1>
          <p style={{ color: "var(--bo-muted)", maxWidth: 820, lineHeight: 1.55 }}>
            Every Friday Brand OS looks at the week's winners, losers and
            proposed changes. The owner reviews each lesson, edits it if
            needed, and accepts or rejects. Accepted lessons flow into the
            Guardian Vault and the next drafts read them automatically.
          </p>
          <ErrorBanner error={state.error} />
          {flash && (
            <div className="bo-flash" style={{ marginTop: 12, padding: 10, borderRadius: 8, background: "var(--bo-accent-soft)" }} data-testid="learning-flash">
              {flash}
            </div>
          )}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 14 }}>
            <Link to="/brand-os/governance" className="bo-btn bo-btn-ghost" data-testid="learning-audit-link">
              View audit log →
            </Link>
            <Link to="/brand-os/vault/guardian" className="bo-btn bo-btn-ghost" data-testid="learning-vault-link">
              Open Guardian Vault →
            </Link>
          </div>
        </section>

        {/* Friday brief summary */}
        <section className="bo-card" style={{ marginTop: 16 }} data-testid="learning-brief">
          <div className="bo-eyebrow">Friday brief · last 7 days</div>
          <h2 className="bo-serif" style={{ fontSize: 20, margin: "4px 0 10px" }}>
            Winners &amp; losers
          </h2>
          {!winners.length && !losers.length ? (
            <EmptyState
              title="No measured variants yet."
              description="As posts go out and insights come back, winners and losers for the last 7 days will show here."
              testId="learning-brief-empty"
            />
          ) : (
            <div style={{ display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))" }}>
              <div data-testid="learning-winners">
                <div style={{ fontWeight: 600, marginBottom: 6 }}>Winners</div>
                {winners.length === 0 && <div style={{ color: "var(--bo-muted)" }}>(none)</div>}
                {winners.map((w) => (
                  <div key={w.variant_id} style={{ padding: 8, borderRadius: 6, background: "var(--bo-panel-alt)", marginBottom: 6 }}>
                    <div style={{ fontFamily: "ui-monospace, monospace", fontSize: 12 }}>
                      {String(w.variant_id).slice(0, 12)}…
                    </div>
                    <div style={{ fontSize: 13, color: "var(--bo-muted)" }}>
                      score {w.score} · sends {w.sends} · engaged {w.engaged_users} · clicks {w.clicks}
                    </div>
                  </div>
                ))}
              </div>
              <div data-testid="learning-losers">
                <div style={{ fontWeight: 600, marginBottom: 6 }}>Losers</div>
                {losers.length === 0 && <div style={{ color: "var(--bo-muted)" }}>(none)</div>}
                {losers.map((w) => (
                  <div key={w.variant_id} style={{ padding: 8, borderRadius: 6, background: "var(--bo-panel-alt)", marginBottom: 6 }}>
                    <div style={{ fontFamily: "ui-monospace, monospace", fontSize: 12 }}>
                      {String(w.variant_id).slice(0, 12)}…
                    </div>
                    <div style={{ fontSize: 13, color: "var(--bo-muted)" }}>
                      score {w.score} · sends {w.sends} · engaged {w.engaged_users} · clicks {w.clicks}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div style={{ fontSize: 12, color: "var(--bo-muted)", marginTop: 12 }}>
            Window ending {friendlyDate(brief?.generated_at)} · pending lessons: {pending.length}
          </div>
        </section>

        {/* Lesson queue */}
        <section className="bo-card" style={{ marginTop: 16 }} data-testid="learning-lesson-queue">
          <div className="bo-eyebrow">Lessons</div>
          <h2 className="bo-serif" style={{ fontSize: 20, margin: "4px 0 10px" }}>
            Proposed changes awaiting your call
          </h2>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
            {["proposed", "accepted", "rejected", "all"].map((f) => (
              <button
                key={f}
                className={`bo-btn ${filter === f ? "bo-btn-primary" : "bo-btn-ghost"}`}
                onClick={() => setFilter(f)}
                data-testid={`learning-filter-${f}`}
              >
                {f === "all" ? "All" : f[0].toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>

          {lessons.length === 0 ? (
            <EmptyState
              title={filter === "proposed"
                ? "Nothing waiting for you right now."
                : `No ${filter} lessons.`}
              description={filter === "proposed"
                ? "When Brand OS spots a repeating pattern — a strong line, a common question, a tired phrase — it will propose a lesson here."
                : "Switch the filter to see other lessons."}
              testId="learning-lessons-empty"
            />
          ) : (
            <div style={{ display: "grid", gap: 10 }}>
              {lessons.map((l) => {
                const isEditing = editing[l.id];
                const isProposed = l.status === "proposed";
                return (
                  <div
                    key={l.id}
                    className="bo-card"
                    style={{ background: "var(--bo-panel-alt)" }}
                    data-testid={`learning-lesson-${l.id}`}
                  >
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 8 }}>
                      <Pill tone={isProposed ? "warn" : l.status === "accepted" ? "success" : "muted"}>
                        {l.status}
                      </Pill>
                      <Pill tone="muted">{KIND_LABEL[l.kind] || l.kind}</Pill>
                      {l.product_id && <Pill tone="muted">product · {String(l.product_id).slice(0, 8)}…</Pill>}
                      <div style={{ fontSize: 12, color: "var(--bo-muted)", marginLeft: "auto" }}>
                        {friendlyDate(l.created_at)}
                      </div>
                    </div>

                    {isEditing && canDecide && isProposed ? (
                      <div style={{ display: "grid", gap: 8 }}>
                        <textarea
                          rows={3}
                          value={isEditing.summary}
                          onChange={(e) => setEditing({ ...editing, [l.id]: { ...isEditing, summary: e.target.value } })}
                          placeholder="Lesson summary"
                          style={{ padding: "8px 10px", borderRadius: 6, border: "1px solid var(--bo-border)", fontSize: 14, lineHeight: 1.5 }}
                          data-testid={`learning-edit-summary-${l.id}`}
                        />
                        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
                            Kind:
                            <select
                              value={isEditing.kind}
                              onChange={(e) => setEditing({ ...editing, [l.id]: { ...isEditing, kind: e.target.value } })}
                              style={{ padding: "6px 8px", borderRadius: 6, border: "1px solid var(--bo-border)" }}
                              data-testid={`learning-edit-kind-${l.id}`}
                            >
                              {KIND_KEYS.map((k) => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}
                            </select>
                          </label>
                          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, flex: "1 1 240px" }}>
                            Tags:
                            <input
                              type="text"
                              value={isEditing.tags}
                              onChange={(e) => setEditing({ ...editing, [l.id]: { ...isEditing, tags: e.target.value } })}
                              placeholder="comma, separated, tags"
                              style={{ flex: 1, padding: "6px 8px", borderRadius: 6, border: "1px solid var(--bo-border)" }}
                              data-testid={`learning-edit-tags-${l.id}`}
                            />
                          </label>
                        </div>
                        <div style={{ display: "flex", gap: 8 }}>
                          <button
                            className="bo-btn bo-btn-primary"
                            onClick={() => saveEdit(l)}
                            disabled={busy || !isEditing.summary.trim()}
                            data-testid={`learning-edit-save-${l.id}`}
                          >
                            Save edits
                          </button>
                          <button
                            className="bo-btn bo-btn-ghost"
                            onClick={() => cancelEditing(l.id)}
                            disabled={busy}
                            data-testid={`learning-edit-cancel-${l.id}`}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div style={{ fontSize: 15, lineHeight: 1.5, marginBottom: 8 }}>
                          {l.summary || <span style={{ color: "var(--bo-muted)" }}>(no summary)</span>}
                        </div>
                        {l.tags && l.tags.length > 0 && (
                          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
                            {l.tags.map((t) => <Pill key={t} tone="muted">#{t}</Pill>)}
                          </div>
                        )}
                        {l.proposed_value !== undefined && l.proposed_value !== null && (
                          <pre style={{
                            margin: "4px 0 8px",
                            padding: 8,
                            borderRadius: 6,
                            background: "var(--bo-panel)",
                            fontSize: 12,
                            overflowX: "auto",
                            maxHeight: 180,
                          }}>
                            {typeof l.proposed_value === "string"
                              ? l.proposed_value
                              : JSON.stringify(l.proposed_value, null, 2)}
                          </pre>
                        )}
                        {!isProposed && l.decision_reason && (
                          <div style={{ fontSize: 13, color: "var(--bo-muted)", marginBottom: 6 }}>
                            Reason: {l.decision_reason}
                          </div>
                        )}
                      </>
                    )}

                    {/* Decision controls (owner only, proposed only, not while editing) */}
                    {canDecide && isProposed && !isEditing && (
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginTop: 6 }}>
                        <input
                          type="text"
                          value={reasonById[l.id] || ""}
                          onChange={(e) => setReasonById({ ...reasonById, [l.id]: e.target.value })}
                          placeholder="Reason (optional, logged)"
                          style={{ flex: "1 1 220px", padding: "6px 10px", borderRadius: 6, border: "1px solid var(--bo-border)", fontSize: 13 }}
                          data-testid={`learning-reason-${l.id}`}
                        />
                        <button
                          className="bo-btn bo-btn-ghost"
                          onClick={() => startEditing(l)}
                          disabled={busy}
                          data-testid={`learning-edit-btn-${l.id}`}
                        >
                          Edit before accepting
                        </button>
                        <button
                          className="bo-btn bo-btn-primary"
                          onClick={() => decide(l, "accept")}
                          disabled={busy}
                          data-testid={`learning-accept-${l.id}`}
                        >
                          Accept
                        </button>
                        <button
                          className="bo-btn"
                          onClick={() => decide(l, "reject")}
                          disabled={busy}
                          data-testid={`learning-reject-${l.id}`}
                          style={{ background: "var(--bo-panel)", borderColor: "var(--bo-border)" }}
                        >
                          Reject
                        </button>
                      </div>
                    )}

                    {!canDecide && isProposed && (
                      <Pill tone="muted">Only the owner can accept or reject.</Pill>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </Shell>
  );
}
